import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DBUser } from "@/lib/db-types";
import { useEmployeeRates } from "@/hooks/use-employee-rates";

export interface EmployeeProfileProject {
  project_id: string;
  project_name: string;
  project_code: string | null;
  project_status: string | null;
  project_phase: string | null;
  is_internal: boolean;
  ownership_pct: number | null;
  budgeted_hours: number;
}

interface RawProjectRef {
  id: string;
  name: string;
  project_code: string | null;
  project_status: string | null;
  project_phase: string | null;
  is_internal: boolean | null;
}

const PROJECT_COLUMNS = "id, name, project_code, project_status, project_phase, is_internal";

export interface EmployeeProfileData {
  user: DBUser;
  projects: EmployeeProfileProject[];
  timeEntries: Array<{
    id: string;
    date: string;
    project_id: string;
    project_name: string;
    task_description: string | null;
    duration_seconds: number;
  }>;
}

/**
 * Fetches complete employee profile data including user details, projects, and recent time entries.
 * Sensitive columns (hourly_cost_rate, hourly_sales_rate, hire_date) are fetched via
 * useEmployeeRates() RPC after column-level REVOKE in migration 20260601160500.
 */
export function useEmployeeProfile(userId: string | null) {
  const ratesQuery = useEmployeeRates();

  const profileQuery = useQuery({
    queryKey: ["employee-profile", userId],
    enabled: !!userId,
    queryFn: async (): Promise<EmployeeProfileData> => {
      if (!userId) throw new Error("User ID required");

      // 1. Fetch user details — omit revoked columns; they are merged from useEmployeeRates
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select(
          "id, full_name, email, role, avatar_url, monthly_capacity_hours, is_active, " +
          "specialization, job_title, phone, target_billing_rate, birthday, created_at"
        )
        .eq("id", userId)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error("User not found");

      // 2. Prosjekter der den ansatte er eier eller har tildelte timer.
      // Soft-slettede prosjekter tas ut i begge oppslagene.
      const [ownershipsRes, assignmentsRes] = await Promise.all([
        supabase
          .from("project_owners")
          .select(`project_id, ownership_pct, projects!inner(${PROJECT_COLUMNS})`)
          .eq("user_id", userId)
          .is("projects.deleted_at", null),
        supabase
          .from("budget_line_assignments")
          .select("hours, budget_lines(project_id)")
          .eq("user_id", userId),
      ]);
      if (ownershipsRes.error) throw ownershipsRes.error;
      if (assignmentsRes.error) throw assignmentsRes.error;

      const hoursMap = new Map<string, number>();
      for (const a of assignmentsRes.data ?? []) {
        const projId = (a.budget_lines as unknown as { project_id: string } | null)?.project_id;
        if (projId) hoursMap.set(projId, (hoursMap.get(projId) ?? 0) + (a.hours ?? 0));
      }

      const toProject = (p: RawProjectRef, ownershipPct: number | null): EmployeeProfileProject => ({
        project_id: p.id,
        project_name: p.name,
        project_code: p.project_code,
        project_status: p.project_status,
        project_phase: p.project_phase,
        is_internal: p.is_internal ?? false,
        ownership_pct: ownershipPct,
        budgeted_hours: hoursMap.get(p.id) ?? 0,
      });

      const projectsMap = new Map<string, EmployeeProfileProject>();
      for (const o of ownershipsRes.data ?? []) {
        const p = o.projects as unknown as RawProjectRef | null;
        if (p) projectsMap.set(p.id, toProject(p, o.ownership_pct));
      }

      const assignmentOnlyIds = [...hoursMap.keys()].filter((id) => !projectsMap.has(id));
      if (assignmentOnlyIds.length > 0) {
        const { data: assignedProjects, error: projErr } = await supabase
          .from("projects")
          .select(PROJECT_COLUMNS)
          .in("id", assignmentOnlyIds)
          .is("deleted_at", null);
        if (projErr) throw projErr;
        for (const p of (assignedProjects ?? []) as RawProjectRef[]) {
          projectsMap.set(p.id, toProject(p, null));
        }
      }

      const projects = Array.from(projectsMap.values()).sort((a, b) =>
        (a.project_code ?? "").localeCompare(b.project_code ?? "", "nb")
      );

      // 3. Fetch time entries for last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const fromDate = thirtyDaysAgo.toISOString().split("T")[0];

      // NB: kolonnen heter start_date (timestamptz), ikke date. Tidligere queries
      // mot "date" feilet stille (error ignorert) → timeloggen var alltid tom.
      const { data: timeEntriesData, error: timeError } = await supabase
        .from("time_entries")
        .select(
          `id, start_date, project_id, task_description, duration_seconds,
           projects(name)`
        )
        .eq("user_id", userId)
        .gte("start_date", fromDate)
        .is("deleted_at", null)
        .order("start_date", { ascending: false });
      if (timeError) throw timeError;

      const timeEntries = (timeEntriesData ?? []).map((te) => ({
        id: te.id,
        date: te.start_date,
        project_id: te.project_id,
        project_name: (te.projects as unknown as { name: string } | null)?.name ?? "Ukjent prosjekt",
        task_description: te.task_description,
        duration_seconds: te.duration_seconds ?? 0,
      }));

      return {
        user: userData as unknown as DBUser,
        projects,
        timeEntries,
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  // Merge rate data from RPC into user object
  const rates = ratesQuery.data?.get(userId ?? "");
  const data = profileQuery.data
    ? {
        ...profileQuery.data,
        user: {
          ...profileQuery.data.user,
          hourly_cost_rate: rates?.hourly_cost_rate ?? null,
          hourly_sales_rate: rates?.hourly_sales_rate ?? null,
          hire_date: rates?.hire_date ?? null,
        } as DBUser,
      }
    : undefined;

  // Profilen styres KUN av profileQuery. Rater er supplerende (admin-visning)
  // og merges inn når klare; en rate-feil skal aldri feile hele profilen.
  return {
    ...profileQuery,
    data,
  };
}
